/**
 * Names the backend packed into one string.
 *
 * "Which students were present last month?" comes back as a single aggregated row — `Students: 50,
 * Student Names: "Abdul Ahadkhan, Abdul Rafay Imran, …"` — and the prose above it repeats the same
 * fifty names in one sentence. Rendered as-is that is a paragraph of names followed by a one-row
 * table whose middle cell is the same paragraph again. These helpers find such a list wherever it
 * landed (a table cell, a narrative sentence), split it back into names, and hand the caller the
 * text with the list taken out, so the names are drawn once, as a list.
 */

/** A cell needs this many names before it is treated as a packed list rather than a single name
 *  that happens to contain a comma. */
export const MIN_CELL_LIST_ITEMS = 4
/** A sentence needs more: a short enumeration ("Ali, Sara and Omar were late") reads fine as
 *  prose, and pulling it into a list would be fussier than leaving it. */
export const MIN_NARRATIVE_LIST_ITEMS = 6
const MAX_NAME_LENGTH = 60

/** Who the list is of, read off the lead clause of the sentence that carried it. Drives the
 *  heading over a list lifted out of prose, which has no column label to borrow. */
export type NameListSubject = 'student' | 'teacher' | 'staff' | 'class' | null

export interface NarrativeNameList {
  /** The narrative with the enumeration sentence removed. May be empty when the sentence was all
   *  there was. */
  text: string
  names: string[]
  subject: NameListSubject
}

/** A name, as opposed to a `label: value` pair, a percentage or a clause: short and free of the
 *  punctuation a statistics dump uses. */
function isNameLike(item: string): boolean {
  // Markdown emphasis/code marks a UI label or setting ("**Display Exam Percentage**"), never a
  // stored name.
  return item.length > 0 && item.length <= MAX_NAME_LENGTH && !/[%;:=|*`]/.test(item)
}

/** Splits "A, B, C, and D" (or "A, B, C and D") into names. Returns `null` when the value is not a
 *  string, or does not split into at least `minItems` name-like entries. */
export function splitNameList(value: unknown, minItems = MIN_CELL_LIST_ITEMS): string[] | null {
  if (typeof value !== 'string') return null
  const text = value
    .trim()
    .replace(/[.。]+$/, '')
    .trim()
  if (!text) return null

  const parts = text
    .split(/\s*,\s*/)
    .map((part) => part.trim())
    .filter(Boolean)
  if (!parts.length) return null

  const last = parts.pop() as string
  const tail = last.replace(/^(?:and|&)\s+/i, '')
  // Without an Oxford comma the last two names arrive joined: "…, Tanveer Sajid and Wendy 9".
  const tailParts = parts.length >= 1 ? tail.split(/\s+(?:and|&)\s+/i) : [tail]
  const items = [...parts, ...tailParts].map((part) => part.trim()).filter(Boolean)

  if (items.length < minItems) return null
  if (!items.every(isNameLike)) return null
  return items
}

/** True when a column holds packed name lists: every non-empty cell is a string that splits into
 *  names, and at least one of them holds `MIN_CELL_LIST_ITEMS` or more. A per-row "Student" column
 *  never qualifies — its cells are single names. */
export function isNameListColumn(key: string, rows: Record<string, unknown>[]): boolean {
  let packed = false
  for (const row of rows) {
    const value = row[key]
    if (value == null || value === '') continue
    if (typeof value !== 'string') return false
    const names = splitNameList(value, 1)
    if (!names) return false
    if (names.length >= MIN_CELL_LIST_ITEMS) packed = true
  }
  return packed
}

/** The words that introduce an enumeration. The split is tried at every occurrence, last first, so
 *  "The students who were present include A, B, …" splits after "include" rather than after
 *  "were" (which would make "present include A" the first name). */
const LEAD_KEYWORDS = /\b(?:includes?|including|are|were|namely|as follows)\b:?|:/gi

/** A sentence end followed by the start of the next sentence. No lookbehind: older Safari rejects
 *  it at parse time, which would take the whole chatbot bundle down with it. */
const SENTENCE_BOUNDARY = /[.!?]\s+(?=[A-Z"(])/g

function splitSentences(line: string): string[] {
  const sentences: string[] = []
  let start = 0
  for (const match of line.matchAll(SENTENCE_BOUNDARY)) {
    const end = match.index + 1 // keep the terminating punctuation with its sentence
    sentences.push(line.slice(start, end))
    start = match.index + match[0].length
  }
  sentences.push(line.slice(start))
  return sentences.filter((sentence) => sentence.trim())
}

function subjectOf(lead: string): NameListSubject {
  if (/\bstudents?\b/i.test(lead)) return 'student'
  if (/\bteachers?\b/i.test(lead)) return 'teacher'
  if (/\bstaff\b/i.test(lead)) return 'staff'
  if (/\b(?:class|classes|sections?|grades?)\b/i.test(lead)) return 'class'
  return null
}

function enumerationOf(sentence: string): {lead: string; names: string[]} | null {
  const cuts: number[] = []
  for (const match of sentence.matchAll(LEAD_KEYWORDS)) {
    cuts.push(match.index + match[0].length)
  }
  for (let i = cuts.length - 1; i >= 0; i -= 1) {
    const names = splitNameList(sentence.slice(cuts[i]), MIN_NARRATIVE_LIST_ITEMS)
    if (names) return {lead: sentence.slice(0, cuts[i]).trim(), names}
  }
  return null
}

/**
 * Finds the first sentence of a narrative that is an enumeration of names ("The students present
 * include A, B, C, …") and returns the names together with the narrative minus that sentence.
 * Returns `null` when no sentence qualifies, so callers render the prose untouched.
 */
export function extractNarrativeNameList(narrative: string): NarrativeNameList | null {
  const lines = (narrative || '').split('\n')

  for (let lineIdx = 0; lineIdx < lines.length; lineIdx += 1) {
    const sentences = splitSentences(lines[lineIdx].trim())
    for (let i = 0; i < sentences.length; i += 1) {
      const found = enumerationOf(sentences[i])
      if (!found) continue
      // Only a list the sentence says is OF PEOPLE OR CLASSES is lifted out. A how-to answer
      // ("Additional options include Display Exam Grade, Multiple Per Page, …") enumerates
      // settings, and drawing those under a "Names" heading was reported as a bug.
      const subject = subjectOf(found.lead)
      if (!subject) continue

      const rest = sentences
        .filter((_, idx) => idx !== i)
        .join(' ')
        .trim()
      const text = [...lines.slice(0, lineIdx), rest, ...lines.slice(lineIdx + 1)]
        .join('\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim()

      return {text, names: found.names, subject}
    }
  }

  return null
}
